import React from 'react';
import type { Message as MessageType } from '../hooks/useChat';

interface MessageProps {
  message: MessageType;
  showTimestamp?: boolean;
}

// Parse inline markdown (bold, italic, links, inline code)
function parseInlineMarkdown(text: string, isUser: boolean): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  let remaining = text;
  let keyIndex = 0;

  while (remaining.length > 0) {
    // Try to match patterns in order of priority
    // Links: [text](url)
    const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) {
      elements.push(
        <a
          key={keyIndex++}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          className={`underline ${isUser ? 'text-white/90 hover:text-white' : 'text-primary hover:text-primary/80'}`}
        >
          {linkMatch[1]}
        </a>
      );
      remaining = remaining.slice(linkMatch[0].length);
      continue;
    }

    // Inline code: `code`
    const codeMatch = remaining.match(/^`([^`]+)`/);
    if (codeMatch) {
      elements.push(
        <code
          key={keyIndex++}
          className={`px-1.5 py-0.5 rounded text-sm ${
            isUser ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-800'
          }`}
        >
          {codeMatch[1]}
        </code>
      );
      remaining = remaining.slice(codeMatch[0].length);
      continue;
    }

    // Bold: **text** or __text__
    const boldMatch = remaining.match(/^(\*\*|__)(.+?)\1/);
    if (boldMatch) {
      elements.push(<strong key={keyIndex++}>{boldMatch[2]}</strong>);
      remaining = remaining.slice(boldMatch[0].length);
      continue;
    }

    // Italic: *text* or _text_ (but not inside words)
    const italicMatch = remaining.match(/^(\*|_)(.+?)\1(?!\w)/);
    if (italicMatch) {
      elements.push(<em key={keyIndex++}>{italicMatch[2]}</em>);
      remaining = remaining.slice(italicMatch[0].length);
      continue;
    }

    // No match, add the next character as plain text
    // Find next potential markdown character
    const nextSpecial = remaining.slice(1).search(/[\[`*_]/);
    if (nextSpecial === -1) {
      elements.push(<span key={keyIndex++}>{remaining}</span>);
      break;
    } else {
      elements.push(<span key={keyIndex++}>{remaining.slice(0, nextSpecial + 1)}</span>);
      remaining = remaining.slice(nextSpecial + 1);
    }
  }

  return elements;
}

// Parse a single line for block-level elements
function parseLine(line: string, index: number, isUser: boolean): React.ReactNode {
  // Headers
  const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
  if (headerMatch) {
    const level = headerMatch[1].length;
    const sizes = ['text-xl font-bold', 'text-lg font-bold', 'text-base font-semibold', 'text-sm font-semibold', 'text-sm font-medium', 'text-sm font-medium'];
    return (
      <div key={index} className={`${sizes[level - 1]} mt-2 mb-1`}>
        {parseInlineMarkdown(headerMatch[2], isUser)}
      </div>
    );
  }

  // Unordered list items
  const ulMatch = line.match(/^[-*+]\s+(.+)$/);
  if (ulMatch) {
    return (
      <div key={index} className="flex items-start gap-2 ml-2">
        <span className="mt-1.5">•</span>
        <span>{parseInlineMarkdown(ulMatch[1], isUser)}</span>
      </div>
    );
  }

  // Ordered list items
  const olMatch = line.match(/^(\d+)[.)]\s+(.+)$/);
  if (olMatch) {
    return (
      <div key={index} className="flex items-start gap-2 ml-2">
        <span className="min-w-[1.5rem]">{olMatch[1]}.</span>
        <span>{parseInlineMarkdown(olMatch[2], isUser)}</span>
      </div>
    );
  }

  // Blockquote
  const quoteMatch = line.match(/^>\s*(.*)$/);
  if (quoteMatch) {
    return (
      <div key={index} className={`border-l-2 pl-3 italic ${isUser ? 'border-white/50' : 'border-gray-300'}`}>
        {parseInlineMarkdown(quoteMatch[1], isUser)}
      </div>
    );
  }

  // Regular line
  if (line.trim() === '') {
    return <div key={index} className="h-2" />;
  }

  return <div key={index}>{parseInlineMarkdown(line, isUser)}</div>;
}

export default function Message({ message, showTimestamp = true }: MessageProps) {
  const isUser = message.role === 'user';

  // Parse markdown content
  const formatContent = (content: string) => {
    // First, handle code blocks
    const parts = content.split(/(```[\s\S]*?```)/g);

    return parts.map((part, partIndex) => {
      if (part.startsWith('```')) {
        const match = part.match(/```(\w+)?\n?([\s\S]*?)```/);
        if (match) {
          const [, language, code] = match;
          return (
            <pre
              key={partIndex}
              className="bg-gray-800 text-gray-100 rounded-lg p-3 text-sm overflow-x-auto my-2"
            >
              {language && (
                <div className="text-xs text-gray-400 mb-2">{language}</div>
              )}
              <code>{code.trim()}</code>
            </pre>
          );
        }
      }

      // Parse remaining content line by line
      const lines = part.split('\n');
      return (
        <div key={partIndex}>
          {lines.map((line, lineIndex) => parseLine(line, lineIndex, isUser))}
        </div>
      );
    });
  };

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} message-enter`}
    >
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
          isUser
            ? 'bg-primary text-white rounded-br-md'
            : 'bg-gray-100 text-gray-800 rounded-bl-md'
        }`}
      >
        <div className="text-sm break-words">
          {formatContent(message.content)}
          {message.isStreaming && (
            <span className="inline-block w-1.5 h-4 bg-current ml-0.5 animate-pulse" />
          )}
        </div>

        {showTimestamp && !message.isStreaming && (
          <div
            className={`text-[10px] mt-1 ${
              isUser ? 'text-white/70' : 'text-gray-400'
            }`}
          >
            {new Date(message.createdAt).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        )}
      </div>
    </div>
  );
}
